import { SwappableDialogRef } from '@common/types/common';
import { ActionCard } from '@components/display/cards';
import { GenericRemoveDialog } from '@components/display/dialogs';
import { DraggableRow } from '@components/display/emblaCarousel';
import { Tabs } from '@components/utility/tabs';
import { AddBadgeDialog } from '@event/components/display/tabs/Badges';
import { useEventDashboardContext } from '@event/contexts';
import { useDeleteLevelBadgeRequirementMutation, useDeleteLevelBadgeRewardMutation } from '@event/services';
import { EventLevelBadgeRequirement, EventLevelBadgeRewards, EventTypes } from '@event/types';
import { formatISODurationToFullString } from '@event/utils';
import { DefaultBadge } from '@levelSystem/components/badges';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { capitalizeFirstLetter, handleError } from '@utils/common';
import clsx from 'clsx';
import { parseISO } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const isBadgeFetching = false; // Placeholder for actual fetching state

const BadgeList = () => {
    const [selectedBadgeId, setSelectedBadgeId] = useState<number | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const isPrivate = useMemo(
        () => [EventTypes.PRIVATE_ONSITE, EventTypes.PRIVATE_VIRTUAL].includes(eventType),
        [eventType]
    );
    const [tab, setTab] = useState<'rewarded' | 'required'>(isPrivate ? 'rewarded' : 'required');

    const removeModalRef = useRef<SwappableDialogRef>(null);
    const addBadgeDialogRef = useRef<SwappableDialogRef & { setTab: (tab: 'rewarded' | 'required') => void }>(null);

    const { badges, isAdmin, isOrganizer, isCohost, cohostPermissions, user } = useEventDashboardContext();
    const { requiredBadges, rewardedBadges } = useMemo(
        () => ({ requiredBadges: badges?.required.data ?? [], rewardedBadges: badges?.rewarded.data ?? [] }),
        [badges]
    );
    const hasBadgePermissions =
        isAdmin || isOrganizer || (isCohost && !!cohostPermissions?.find((perm) => perm.role === user?.role)?.edit);
    const { isRequiredBadgesLoading, isRewardedBadgesLoading } = useMemo(
        () => ({
            isRequiredBadgesLoading: badges?.required.isLoading ?? false,
            isRewardedBadgesLoading: badges?.rewarded.isLoading ?? false,
        }),
        [badges]
    );

    const [deleteLevelBadgeRequirement, { isLoading: isDeletingRequirement }] =
        useDeleteLevelBadgeRequirementMutation();
    const [deleteLevelBadgeReward, { isLoading: isDeletingReward }] = useDeleteLevelBadgeRewardMutation();
    const currentBadges: EventLevelBadgeRequirement[] | EventLevelBadgeRewards[] =
        tab === 'required' ? requiredBadges : rewardedBadges;

    const { event } = useEventDashboardContext();

    const hasEventEnded = useMemo(
        () => (event?.date_end ? new Date() >= parseISO(event.date_end) : true),
        [event?.date_end]
    );

    const handleAddBadgeClick = () => {
        setIsDialogOpen(true);
        addBadgeDialogRef.current?.setTab(tab);
    };

    const handleRemoveBadge = async () => {
        if (!selectedBadgeId) return;

        try {
            if (tab === 'required') {
                await deleteLevelBadgeRequirement({
                    type: eventType,
                    id: Number(eventId),
                    level_badge_requirement_id: selectedBadgeId,
                }).unwrap();
            } else {
                await deleteLevelBadgeReward({
                    type: eventType,
                    id: Number(eventId),
                    level_badge_reward_id: selectedBadgeId,
                }).unwrap();
            }
            removeModalRef.current?.swapScreen('success');
        } catch (err) {
            console.error('Error removing badge:', err);
            handleError('An error occurred while removing the badge. Please try again later.', err);
        }
    };

    const handleRemoveDialogClose = () => {
        setSelectedBadgeId(null);
        removeModalRef.current?.swapScreen('main');
    };

    return (
        <>
            <Card className="rounded-lg black-border mt-9">
                <CardContent>
                    <div className="flex">
                        <Tabs
                            boxProps={{
                                className: 'w-full h-min',
                            }}
                            className="gap-5"
                            value={tab}
                            onChange={(_, newValue) => setTab(newValue)}
                        >
                            <Tabs.Tab
                                className={clsx('block', { hidden: isPrivate })}
                                label={
                                    <>
                                        Required Badge
                                        {Number(requiredBadges?.length) > 0 ? (
                                            <>
                                                {' '}
                                                <Typography
                                                    component="span"
                                                    color="black40.main"
                                                    fontSize={14}
                                                    fontWeight={500}
                                                >
                                                    {requiredBadges?.length || 0} created
                                                </Typography>
                                            </>
                                        ) : null}
                                    </>
                                }
                                value="required"
                            />
                            <Tabs.Tab
                                className="block"
                                label={
                                    <>
                                        Rewarded Badge
                                        {Number(rewardedBadges?.length) > 0 ? (
                                            <>
                                                {' '}
                                                <Typography
                                                    component="span"
                                                    color="black40.main"
                                                    fontSize={14}
                                                    fontWeight={500}
                                                >
                                                    {rewardedBadges?.length || 0} created
                                                </Typography>
                                            </>
                                        ) : null}
                                    </>
                                }
                                value="rewarded"
                            />
                        </Tabs>
                        <Button
                            className="rounded-full black-border ml-5 min-w-fit"
                            disabled={(tab === 'required' && hasEventEnded) || !hasBadgePermissions}
                            onClick={handleAddBadgeClick}
                        >
                            Add Badge
                        </Button>
                    </div>
                    <div className="min-h-[230px] relative">
                        {isRequiredBadgesLoading || isRewardedBadgesLoading ? (
                            <CircularProgress className="absolute-center" />
                        ) : null}
                        <div className={clsx({ blur: isBadgeFetching, 'absolute-center': !currentBadges.length })}>
                            {currentBadges.length ? (
                                <DraggableRow
                                    className="pt-8"
                                    items={currentBadges.map((badge) => (
                                        <ActionCard
                                            key={badge.id}
                                            closeButtonProps={{
                                                onClick: () => setSelectedBadgeId(badge.id),
                                                TooltipProps: {
                                                    title: 'Remove Badge',
                                                },
                                                hidden: !hasBadgePermissions,
                                            }}
                                            img={
                                                badge.level_badge.image ? (
                                                    <img src={badge.level_badge.image} />
                                                ) : (
                                                    <DefaultBadge
                                                        className="!size-28"
                                                        level={badge.level_badge.level.value}
                                                        badgeCoreProps={{
                                                            className: '!size-14',
                                                        }}
                                                    />
                                                )
                                            }
                                            typographyElements={[
                                                <Typography fontWeight={500}>
                                                    {capitalizeFirstLetter(badge.level_badge.level.name)} Level
                                                </Typography>,
                                            ]}
                                            chips={[
                                                <Chip
                                                    className="blue-gradient"
                                                    classes={{
                                                        label: 'py-2 px-[10px]',
                                                    }}
                                                    label={
                                                        <Typography fontSize={12} fontWeight={500} color="white.main">
                                                            {capitalizeFirstLetter(badge.level_badge.badge.name)}
                                                        </Typography>
                                                    }
                                                    size="small"
                                                />,
                                                ...('timeframe' in badge && badge.timeframe
                                                    ? [
                                                          <Chip
                                                              className="blue-gradient"
                                                              classes={{
                                                                  label: 'py-2 px-[10px]',
                                                              }}
                                                              label={
                                                                  <Typography
                                                                      fontSize={12}
                                                                      fontWeight={500}
                                                                      color="white.main"
                                                                  >
                                                                      {formatISODurationToFullString(badge.timeframe)}
                                                                  </Typography>
                                                              }
                                                              size="small"
                                                          />,
                                                      ]
                                                    : []),
                                            ]}
                                        />
                                    ))}
                                />
                            ) : !isRequiredBadgesLoading && !isRewardedBadgesLoading ? (
                                <div className="bg-neutral-900/10 p-4 rounded-md w-fit">
                                    <Typography className="font-medium">
                                        No event {tab} badges have been created
                                    </Typography>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </CardContent>
            </Card>
            <AddBadgeDialog ref={addBadgeDialogRef} open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
            <GenericRemoveDialog
                ref={removeModalRef}
                open={!!selectedBadgeId}
                titleSX={
                    <>
                        <Typography
                            fontFamily="museo-sans"
                            fontSize={32}
                            fontWeight={500}
                            letterSpacing="-0.8px"
                            textAlign="center"
                        >
                            Are you sure you want to remove this badge?
                        </Typography>
                        <Typography fontSize={16} textAlign="center">
                            By clicking “Remove“, the badge will no longer be an{' '}
                            {tab === 'rewarded' ? 'event reward' : 'entry requirement'}.
                        </Typography>
                    </>
                }
                primaryBtnProps={{
                    title: 'Remove',
                    onClick: handleRemoveBadge,
                    startIcon:
                        isDeletingRequirement || isDeletingReward ? <CircularProgress size={20} /> : <DeleteIcon />,
                    disabled: isDeletingRequirement || isDeletingReward,
                }}
                secondaryBtnProps={{
                    title: 'Go Back',
                    onClick: handleRemoveDialogClose,
                    startIcon: <ArrowBackIcon />,
                    disabled: isDeletingRequirement || isDeletingReward,
                }}
                onClose={handleRemoveDialogClose}
                successDescription="Badge has been successfully removed."
            />
        </>
    );
};
export { BadgeList };
